import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'

// Actions
import { actions as audioActions } from 'modules/audio/actions'

// Components
import { EmptyState, useMaxWidth } from '@tracklib/kit'
import ErrorBoundary from 'errorboundary'
import Recommendations from 'components/browse/recommendations'
import VirtualList from 'components/browse/virtuallist'
import Track from 'components/track'
import Tinder from 'components/tinder/tinder'
import IsStaff from 'components/base/isstaff'

// Hooks
import { useDisplayMode } from 'modules/browse/hooks'

// Selectors
import { selectTrackListFromTrackIds } from 'modules/browse/selectors'

// Types
import { ApiRequest, Artist, Collection, Loop, TrackList as TrackListType, State, DisplayMode } from 'types'

// Utils
import { isBrowser } from '@process'
import { createSongItemId } from 'utils'

import styles from './tracklist.css'

export const TRACKS_PER_PAGE = 20

type TrackListProps = {
  /* eslint-disable */
  trackIds: Array<{
    id: number
    loop?: Loop | null
  }>
  recommendedTrackIds: Array<number>
  /* eslint-enable */
  recommendedArtists: Array<Artist>
  recommendedCollections: Array<Collection>
  contextKey: string
  request: ApiRequest
  setAsActivePlaylistOnMount?: boolean
  mode?: DisplayMode
  renderSpecificTrackNames?: boolean
  renderOnlySpecificTrackName?: boolean
  emptyStateMessage?: string
  totalTrackCount: number
  maxCount?: number
  hideRecommendations?: boolean
  loadMore?: (page: number) => void
  lastPageLoaded?: number
  recommendationsRequest: ApiRequest
}

const TrackList = (props: TrackListProps): JSX.Element => {
  const {
    recommendedArtists = [],
    recommendedCollections = [],
    recommendedTrackIds = [],
    recommendationsRequest,
    setAsActivePlaylistOnMount = false,
    trackIds,
    maxCount,
    totalTrackCount,
    hideRecommendations,
    request,
  } = props

  const dispatch = useDispatch()
  const displayMode = useDisplayMode()

  const { search } = useLocation()

  const state = useSelector<State, State>(s => s)

  const trackList = selectTrackListFromTrackIds(state, trackIds).filter((x, i) => (maxCount ? i < maxCount : true))
  const recommendedTrackList = selectTrackListFromTrackIds(
    state,
    recommendedTrackIds?.map(id => ({ id })),
  )
  const [contextKey, setContextKey] = useState(
    props.contextKey || (window.performance ? String(window.performance.now()).split('.')[0] : String(Math.random()).replace('.', '')),
  )

  const setPlaylist = (playlist: TrackListType, setAsActivePlaylist: boolean) => {
    dispatch(audioActions.setPlaylist(playlist, contextKey, setAsActivePlaylist))
  }

  const clearPlaylist = () => dispatch(audioActions.clearPlaylist(contextKey))

  useEffect(() => {
    if (recommendedTrackList.length) {
      clearPlaylist()
      setPlaylist([...trackList, ...recommendedTrackList], setAsActivePlaylistOnMount)
      setContextKey(contextKey)
    }
  }, [contextKey, request.isDone, recommendedTrackList.length]) // eslint-disable-line

  const handleLoadBegin = () => {
    const { loadMore, lastPageLoaded } = props
    if (!request.isLoading && !request.hasError && trackList.length < totalTrackCount && loadMore) {
      loadMore((lastPageLoaded || 0) + 1)
    }
  }

  const getDisplayMode = (): DisplayMode => {
    const { mode } = props
    return mode || displayMode
  }

  const isXSmall = useMaxWidth(480)

  const renderTrackList = (
    list?: TrackListType,
    fakeTracklist?: Array<number>,
    preventOnlySpecificTrackName?: boolean,
  ): Array<JSX.Element> | null => {
    const { renderSpecificTrackNames, renderOnlySpecificTrackName } = props
    const parsedFakeTrackList: TrackListType = (fakeTracklist || [])?.map(trackId => ({ trackId, loop: null, songId: 0 }))
    if (!parsedFakeTrackList && !list) {
      return null
    }
    return (list || parsedFakeTrackList).map(({ trackId, loop }, index) => {
      const key = `${contextKey}-${fakeTracklist && 'fake'}-${String(index)}`
      return (
        <ErrorBoundary hidden key={key}>
          <Track
            trackId={trackId}
            songItemId={createSongItemId(trackId, loop)}
            loop={loop}
            renderSpecificTrackNames={renderSpecificTrackNames}
            renderOnlySpecificTrackName={!preventOnlySpecificTrackName && renderOnlySpecificTrackName}
            contextKey={contextKey}
            mode={getDisplayMode()}
          />
        </ErrorBoundary>
      )
    })
  }

  const renderContent = (hasMore: boolean) => {
    const { emptyStateMessage } = props
    if (trackList.filter(({ trackId, songId }) => trackId && songId).length === 0 && request.isDone) {
      return <EmptyState text={emptyStateMessage || 'No tracks.'} emoji="looking" />
    }
    const fakeTrackList = Array.from({ length: maxCount === 1 ? 0 : maxCount || 10 }, (_, i) => ({ songId: 1, trackId: i }))
    const listOfTracks: TrackListType = [...trackList, ...(request.isLoading || (!isBrowser && !trackList.length) ? fakeTrackList : [])]
    const tracksToRender = renderTrackList(listOfTracks)
    if (!isBrowser || (maxCount && maxCount < TRACKS_PER_PAGE) || !totalTrackCount || totalTrackCount < TRACKS_PER_PAGE) {
      // NOTE(kitti): VirtualList doesnt work on server
      return <div className={styles.fakeInfinity}>{tracksToRender}</div>
    }
    const getItemHeight = () => {
      if (isXSmall) {
        return 76
      }
      if (displayMode === 'compact' || displayMode === 'extraCompact') {
        return 88
      }
      return 189
    }
    return <VirtualList itemBuffer={5} itemHeight={getItemHeight()} items={tracksToRender} loadMore={handleLoadBegin} hasMore={hasMore} />
  }

  const hasMore: boolean = totalTrackCount > 0 && trackList.length < totalTrackCount && trackList.length < 10000 - TRACKS_PER_PAGE
  const showRecommendations = !hideRecommendations && !request.isLoading && !hasMore

  return (
    <>
      {search.includes('swipe') && (
        <IsStaff>
          <Tinder trackListProps={props} />
        </IsStaff>
      )}
      <div className={styles.trackList}>
        {renderContent(hasMore)}
        {showRecommendations && (
          <Recommendations
            recommendedTracks={recommendedTrackList}
            recommendedArtists={recommendedArtists}
            recommendedCollections={recommendedCollections}
            renderTrackList={renderTrackList}
            request={recommendationsRequest}
          />
        )}
      </div>
    </>
  )
}

export default TrackList
